import { auditlogApi } from './auditlog-api';
import type { LogMaster, LogMasterCreateRequest, LogMasterUpdateRequest, LogMasterQueryParams, DeleteResponse } from '../types';

// API prefix for log master endpoints
const BASE = '/api/v1/log-master';

// Response wrapper from API
interface ApiResponse<T> {
  success: boolean;
  code: number;
  message: string;
  data: T;
}

/** Re-scope preview shape (log templates have no referrers → always rescopable). */
export interface LogMasterRescopePreview {
  canRescope: boolean;
  conflicts: Record<string, Record<string, number>>;
  references: Record<string, Record<string, number>>;
}

// Paginated response structure
interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export const logMasterService = {
  /**
   * Get all log masters with filters
   * GET /api/v1/log-master/list
   */
  getAll: async (params?: LogMasterQueryParams): Promise<LogMaster[]> => {
    const queryParams: Record<string, unknown> = {};

    if (params?.logCode) queryParams.logCode = params.logCode;
    if (params?.search) queryParams.search = params.search;
    if (params?.layer) queryParams.layer = params.layer;
    if (params?.logtype) queryParams.logtype = params.logtype;
    if (params?.include_deleted !== undefined) {
      queryParams.include_deleted = params.include_deleted;
    } else {
      queryParams.include_deleted = false;
    }
    if (params?.page) queryParams.page = params.page;
    if (params?.page_size) queryParams.page_size = params.page_size;

    const response = await auditlogApi.get<ApiResponse<PaginatedResponse<LogMaster>>>(`${BASE}/list`, { params: queryParams });
    return response.data.data?.items || [];
  },

  /**
   * Get all log masters with pagination info
   */
  getAllPaginated: async (params?: LogMasterQueryParams): Promise<PaginatedResponse<LogMaster>> => {
    const queryParams: Record<string, unknown> = {};

    if (params?.logCode) queryParams.logCode = params.logCode;
    if (params?.search) queryParams.search = params.search;
    if (params?.layer) queryParams.layer = params.layer;
    if (params?.logtype) queryParams.logtype = params.logtype;
    if (params?.include_deleted !== undefined) {
      queryParams.include_deleted = params.include_deleted;
    } else {
      queryParams.include_deleted = false;
    }
    if (params?.page) queryParams.page = params.page;
    if (params?.page_size) queryParams.page_size = params.page_size;
    if (params?.filters) queryParams.filters = params.filters;
    if (params?.app_id) queryParams.app_id = params.app_id;
    if (params?.include_global) queryParams.include_global = true;

    const response = await auditlogApi.get<ApiResponse<PaginatedResponse<LogMaster>>>(`${BASE}/list`, { params: queryParams });
    return response.data.data || { items: [], total: 0, page: 1, page_size: 10, total_pages: 0 };
  },

  /**
   * Get all log masters for export
   */
  getAllForExport: async (params?: Omit<LogMasterQueryParams, 'page' | 'page_size'>, page_size: number = 1000): Promise<LogMaster[]> => {
    const queryParams: Record<string, unknown> = {
      page_size,
      include_deleted: false,
    };

    if (params?.logCode) queryParams.logCode = params.logCode;
    if (params?.search) queryParams.search = params.search;
    if (params?.layer) queryParams.layer = params.layer;
    if (params?.logtype) queryParams.logtype = params.logtype;
    if (params?.include_deleted !== undefined) queryParams.include_deleted = params.include_deleted;
    if (params?.filters) queryParams.filters = params.filters;

    const response = await auditlogApi.get<ApiResponse<PaginatedResponse<LogMaster>>>(`${BASE}/list`, { params: queryParams });
    return response.data.data?.items || [];
  },

  /**
   * Get log master by ID
   * GET /api/v1/log-master/get?id={id}
   */
  getById: async (id: string): Promise<LogMaster> => {
    const response = await auditlogApi.get<ApiResponse<LogMaster>>(`${BASE}/get`, { params: { id } });
    return response.data.data;
  },

  /**
   * Create new log master
   * POST /api/v1/log-master/create
   */
  create: async (payload: LogMasterCreateRequest): Promise<LogMaster> => {
    const response = await auditlogApi.post<ApiResponse<LogMaster>>(`${BASE}/create`, payload);
    return response.data.data;
  },

  /**
   * Update log master
   * PUT /api/v1/log-master/update?id={id}
   */
  update: async (id: string, payload: LogMasterUpdateRequest): Promise<LogMaster> => {
    const response = await auditlogApi.put<ApiResponse<LogMaster>>(`${BASE}/update`, payload, { params: { id } });
    return response.data.data;
  },

  /**
   * Delete log master (soft delete)
   * DELETE /api/v1/log-master/delete?id={id}
   */
  delete: async (id: string): Promise<DeleteResponse> => {
    const response = await auditlogApi.delete<DeleteResponse>(`${BASE}/delete`, { params: { id } });
    return response.data;
  },

  /**
   * Preview re-scoping a log master to a project (empty appId = global).
   * GET /api/v1/log-master/{id}/rescope-preview
   */
  getRescopePreview: async (id: string, appId?: string | null): Promise<LogMasterRescopePreview> => {
    const response = await auditlogApi.get<ApiResponse<LogMasterRescopePreview>>(
      `${BASE}/${id}/rescope-preview`,
      { params: { app_id: appId || undefined } }
    );
    return response.data.data;
  },

  /**
   * Change a log master's owning project (null/empty = global). Platform admin only.
   * PATCH /api/v1/log-master/{id}/project
   */
  rescopeProject: async (id: string, appId: string | null): Promise<LogMaster> => {
    const response = await auditlogApi.patch<ApiResponse<LogMaster>>(
      `${BASE}/${id}/project`,
      { appId: appId || null }
    );
    return response.data.data;
  },

  /**
   * Bulk create log masters
   * POST /api/v1/log-master/bulk-create
   */
  bulkCreate: async (items: LogMasterCreateRequest[]): Promise<{
    success: LogMaster[];
    failed: Array<{ index: number; logCode: string; error: string }>;
    totalSuccess: number;
    totalFailed: number;
  }> => {
    const response = await auditlogApi.post<ApiResponse<{
      success: LogMaster[];
      failed: Array<{ index: number; logCode: string; error: string }>;
      totalSuccess: number;
      totalFailed: number;
    }>>(`${BASE}/bulk-create`, { items });
    return response.data.data;
  },
};
